/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import * as React from 'react';
import { pluralize } from 'sarsaparilla';

interface SearchPaginationTextProps {
    start?: number;
    total?: number;
    pageSize?: number;
    placename?: string;
    isLoading?: boolean;
    maxResults?: number;
    children?: React.ReactNode;
}

export default function SearchPaginationText({
    start: startProp = 0,
    total: totalProp = 0,
    pageSize: pageSizeProp = 20,
    placename,
    isLoading,
    maxResults = 300,
    children,
}: SearchPaginationTextProps) {
    // Because some silly people are sending null values ...
    const start = startProp ?? 0;
    const total = totalProp ?? 0;
    let pageSize = pageSizeProp ?? 20;

    if (isLoading) {
        return (
            <div className="search-pagination-text">
                <span>Loading search results ...</span>
            </div>
        );
    }

    let totalStr = total.toString();

    if (total > maxResults) {
        totalStr = `${maxResults}+`;
    }

    if (total < pageSize) {
        pageSize = total;
    }

    if (start + pageSize > total) {
        pageSize -= Math.abs(total - pageSize - start);
    }

    const startNum = start + 1;
    const endNum = start + pageSize;

    if (total <= 0) {
        return null;
    }

    return (
        <div className="search-pagination-text">
            <span>
                {endNum === total && total <= pageSize
                    ? `${total} ${pluralize(total, 'result', 'results')}`
                    : `${startNum} - ${endNum} results of ${totalStr}`}
                {placename && <span>{` near ${placename}`}</span>}
            </span>

            {children}
        </div>
    );
}
